/***
 * Created on 2025/2/5
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useState} from 'react';
import ic_device_status_online from '../../static/img/utils/ic-device-status-online.svg';
import ic_device_status_offline from '../../static/img/utils/ic-device-status-offline.svg';
import ic_sys_warning from '../../static/img/utils/ic-sys-warning.svg';
import ts_431p from '../../static/img/temp/ts-431p.svg';
import {useTranslation} from "react-i18next";
import {apiDelete, apiGet, apiPost} from "../../service/api";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import semver from "semver";

function IftttDeviceItem({device, state, dispatch, isSingleDevice}) {
    const { t } = useTranslation();
    const [buttonText, setButtonText] = useState('');
    const [isProcessing, setIsProcessing] = useState(true);
    const queryClient = useQueryClient();

    const checkQPKGUpdateVersion = (install_version) => {
        const _normalizeVersion = (version) => version.split('.').map(num => parseInt(num, 10)).join('.');
        const base_version = window.acc_mf.auth.DAUTH_UPDATE_VERSION_MAPPING[device.app_id];
        return semver.gt(_normalizeVersion(install_version), _normalizeVersion(base_version));
    }

    const {data, error, isFetching, refetch} = useQuery({
        queryKey: ['dauth_qpkg_check', device.device_id],
        queryFn: async () => {
            let result = null;
            try {
                result = await apiGet(`/ajax/dauth/qpkg/check?device_id=${device.device_id}${device?.auth_id ? `&auth_id=${device.auth_id}` : ''}&package_id=${device.package_ids[0]}`)
                if (device.authorized) {
                    if (result?.installed) {
                        if (result?.enabled) {
                            if (!checkQPKGUpdateVersion(result.version)) {
                                setButtonText('@Update@');
                            }
                            else {
                                await bindDevice(result);
                                setButtonText('@Disconnect@');
                            }
                        }
                        else {
                            setButtonText('@Enable@');
                        }
                    }
                    else {
                        setButtonText('@Install@');
                    }
                }
                else {
                    if (!result.enabled) {
                        setButtonText('warning');
                    }
                    else {
                        setButtonText('@Connect@');
                    }
                }
            } catch (e) {
                if (e.cause.code === 1129005) {
                    setButtonText('@Reconnect@');
                }
                if (e.cause.code === 1190001) {
                    setButtonText('@Connect@');
                }
                else {
                    setButtonText('warning');
                }
            }
            setIsProcessing(false);
            return result;
        }
    });

    if (error) {
        console.log(error.cause);
    }

    const bindDeviceMutate = useMutation({
        mutationFn: async (data) => await apiPost(`/ajax/dauth/authorization/bind`, data),
        onSuccess: data => {},
        onError: (error, variables, context) => {}
    });

    const bindDevice = async (qpkg) => {
        const data = {
            auth_id: device.auth_id
        }
        if (!qpkg?.bind) {
            await bindDeviceMutate.mutateAsync(data);
        }
    }

    const logoutDeviceMutate = useMutation({
        mutationFn: async () => await apiDelete(`/ajax/dauth/authorization/${device.auth_id}`, null),
        onSuccess: async (data) => {
            await queryClient.invalidateQueries('dauth_authorization_status');
            await queryClient.refetchQueries('dauth_authorization_status');
            // await refetch();
            setIsProcessing(false);
        }
    });

    const logoutDevice = async () => {
        setIsProcessing(true);
        setButtonText('');
        await logoutDeviceMutate.mutateAsync();
    };

    const enableQPKGMutate = useMutation({
        mutationFn: async (data) => await apiPost(`/ajax/dauth/qpkg/${device.package_ids[0]}/enable`, data),
        onSuccess: async (data) => {
            await refetch();
            setIsProcessing(false);
        },
        onError: (error, variables, context) => {
            console.log(error);
        }
    });

    const enableQPKG = async () => {
        const data = {
            auth_id: device.auth_id,
        }
        setIsProcessing(true);
        setButtonText('@Enabling...@');
        await enableQPKGMutate.mutateAsync(data);
    }

    const installQPKGMutate = useMutation({
        mutationFn: async (data) => await apiPost(`/ajax/dauth/qpkg/${device.package_ids[0]}/install`, data),
        onSuccess: async (data) => {
            await refetch();
            setIsProcessing(false);
        },
        onError: (error, variables, context) => {
            console.log(error);
        }
    });

    const installQPKG = async () => {
        const data = {
            auth_id: device.auth_id,
        }
        setIsProcessing(true);
        setButtonText('@Installing...@');
        await installQPKGMutate.mutateAsync(data);
    };

    const updateQPKGMutate = useMutation({
        mutationFn: async (data) => await apiPost(`/ajax/dauth/qpkg/${device.package_ids[0]}/update`, data),
        onSuccess: async (data) => {
            await refetch();
            setIsProcessing(false);
        },
        onError: (error, variables, context) => {
            console.log(error);
        }
    });

    const updateQPKG = async () => {
        const data = {
            auth_id: device.auth_id,
        }
        setIsProcessing(true);
        setButtonText('@Installing...@');
        await updateQPKGMutate.mutateAsync(data);
    };

    const onButtonClick = async () => {
        if (buttonText === '@Disconnect@') {
            await logoutDevice();
        }
        else if (buttonText === '@Connect@') {
            dispatch({type: 'toggleLoginDevice', payload: true, data: device});
        }
        else if (buttonText === '@Enable@') {
            await enableQPKG();
        }
        else if (buttonText === '@Install@') {
            await installQPKG();
        }
        else if (buttonText === '@Update@') {
            await updateQPKG();
        }
    }

    useEffect(() => {
        if (buttonText === '@Connect@' && isSingleDevice) {
            dispatch({type: 'toggleLoginDevice', payload: true, data: device});
        }

    }, [buttonText, device]);

    return (
        <div className="common-info-grid__row">
            <div className="device-group">
                <div className="device-status"><img src={data ? ic_device_status_online : ic_device_status_offline}/></div>
                <div className="device-img"><img src={ts_431p}/></div>
                <div className="device-name">{device.device_name}</div>
            </div>
            <div className="device-action">
                {isProcessing &&
                    <div className="display-inline-flex align-items-center">
                        {t(buttonText)}
                        <div className="loading-indicator__anime--circular !w-18px !h-18px !p-0 !ml-10">
                            <svg className="circular" viewBox="0 0 40 40">
                                <circle className="base" cx="20" cy="20" r="16" fill="none" strokeLinecap="round" strokeWidth="4" strokeMiterlimit="10"
                                        stroke="#e5e8eb"/>
                                <circle className="path" cx="20" cy="20" r="16" fill="none" strokeLinecap="round" strokeWidth="4"
                                        strokeMiterlimit="10" stroke="#001B49"/>
                            </svg>
                        </div>
                    </div>
                }

                {!isProcessing && buttonText === 'warning' &&
                    <>
                        <img src={ic_sys_warning} className="w-20px h-20px"
                             data-tooltip-id="dauth-connect-error"
                             data-tooltip-content={t('@Unable to connect to this device remotely. Please make sure the CloudLink QPKG is installed and running correctly on this device.@')}
                             alt='Unable to connect'
                        />
                    </>
                }

                {!isProcessing && buttonText !== 'warning' &&
                    <button className={`q-btn q-btn--primary btn-small btn-rounded-full ${buttonText==='@Disconnect@' ? '!color-red' : ''}`} onClick={onButtonClick}>
                        {t(buttonText)} {['@Enable@', '@Install@', '@Update@'].includes(buttonText) ? 'IFTTT' : ''}
                    </button>
                }
            </div>
        </div>
    );
}

export default IftttDeviceItem;